import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { news, newsTags } from "@/mocks/news";
import NewsCard from "@/components/feature/NewsCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 5;

export default function NewsCategory() {
  usePageMeta({
    title: "韩剧新闻 - 韩剧TV",
    description:
      "韩剧新闻栏目每日更新最新韩剧动态，涵盖新剧发布、演员动态、剧情解析与行业观察，帮你第一时间掌握韩剧资讯。",
    keywords: "韩剧新闻,韩剧资讯,韩剧动态,新剧发布,韩剧解析",
  });

  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState<"newest" | "hot">("newest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = news.filter((n) => (tag === "全部" ? true : n.tag === tag));
    list = [...list].sort((a, b) =>
      sort === "newest" ? (a.date < b.date ? 1 : -1) : b.id - a.id
    );
    return list;
  }, [tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setTag("全部");
    setSort("newest");
    setPage(1);
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "韩剧资讯" }]} />

      <header className="mt-6">
        <h1 className="font-heading text-2xl font-800 text-foreground-950 md:text-4xl">韩剧新闻</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          聚焦最新韩剧动态，从新剧开播、演员近况到深度剧情解析，我们用清晰、及时、有料的内容，
          陪你看懂每一部值得关注的韩剧。栏目内容每日更新。
        </p>
      </header>

      <div className="mt-8 flex flex-col gap-4 border-b border-background-200/70 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {newsTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTag(t);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                tag === t
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 self-start rounded-full bg-background-100 p-1">
          {([
            { key: "newest", label: "最新" },
            { key: "hot", label: "最热" },
          ] as const).map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                setSort(s.key);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                sort === s.key
                  ? "bg-background-50 text-primary-600 shadow-none"
                  : "text-foreground-600"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-4 text-sm text-foreground-500">
        共 <span className="font-600 text-foreground-800">{filtered.length}</span> 篇资讯
      </p>

      {pageItems.length > 0 ? (
        <div className="mt-6 space-y-4">
          {pageItems.map((item) => (
            <NewsCard key={item.id} item={item} variant="list" />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 py-20 text-center">
          <i className="ri-inbox-line text-5xl text-foreground-300"></i>
          <p className="mt-4 text-base font-500 text-foreground-800">暂无匹配的资讯</p>
          <p className="mt-2 text-sm text-foreground-500">换个标签看看，或返回首页浏览推荐内容。</p>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={resetFilters}
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 cursor-pointer hover:bg-primary-600"
            >
              重置筛选
            </button>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 cursor-pointer hover:bg-background-100"
            >
              返回首页
            </Link>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i + 1)}
              className={`h-9 w-9 rounded-md text-sm transition-colors cursor-pointer ${
                currentPage === i + 1
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:bg-background-100"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      )}
    </div>
  );
}